/**
 * FRONTIER F9-4 — Adaptive role-personalized UI: the honest usage signal.
 *
 * The shell has no server-side per-user *navigation*-usage counter (the real
 * per-user personalization store the platform ships is the Widgets layout API,
 * `GET/PUT /api/v1/widgets/layouts/{routeKey}`, consumed by LandingPageEditor).
 * So the ADAPTIVE ORDERING signal is modelled honestly on locally-tracked,
 * in-session usage: every time the operator opens a surface we bump a counter
 * for its owning module id. Nothing here is fabricated — with no interactions
 * yet the store is empty and the adaptation layer falls back to the default
 * ordering (honest cold-start, not a random/fake personalization).
 *
 * The counts are persisted to localStorage under a versioned, tenant- AND
 * persona-scoped key (mirroring the sidebar-collapse convention in AppShell) so
 * one persona's learned ordering never bleeds into another's, and a shared
 * workstation keeps each tenant's signal separate.
 *
 * IMPORTANT (entitlement boundary): this store records *usage frequency only*.
 * It has no concept of what a principal is entitled to and never grants access.
 * Ranking is applied downstream, strictly to the already-permitted set the
 * registries + entitlement evaluator return (see `adaptiveNav.ts`). A count for
 * a module the user is no longer entitled to is simply never consulted, because
 * that module is not present in the permitted list the ranker receives.
 */

import { useSyncExternalStore } from 'react';

/** Map of surface key (module id) → cumulative open count for the active scope. */
export type UsageCounts = Readonly<Record<string, number>>;

const KEY_PREFIX = 'tyndo.adaptive.usage.v1';
const RESET_PREFIX = 'tyndo.adaptive.optOut.v1';

/**
 * Scope the learned signal by tenant + persona so adaptation is truly
 * *role*-personalized and never leaks across principals on a shared station.
 * A null persona (no persona workspace selected) uses a shared bucket so the
 * ISA-95 / module / industry modes still learn a single default profile.
 */
export interface UsageScope {
  tenantId: string | null | undefined;
  persona: string | null | undefined;
}

function usageKeyFor(scope: UsageScope): string {
  return `${KEY_PREFIX}.${scope.tenantId ?? 'shared'}.${scope.persona ?? 'default'}`;
}

function optOutKeyFor(scope: UsageScope): string {
  return `${RESET_PREFIX}.${scope.tenantId ?? 'shared'}.${scope.persona ?? 'default'}`;
}

// A single in-memory cache keyed by the resolved storage key. useSyncExternalStore
// requires a stable snapshot reference between renders when nothing changed, so
// we memoize the parsed object and only replace it when we actually mutate.
const snapshotCache = new Map<string, UsageCounts>();
const EMPTY: UsageCounts = Object.freeze({});
const listeners = new Set<() => void>();

function readRaw(key: string): UsageCounts {
  const cached = snapshotCache.get(key);
  if (cached) return cached;
  let parsed: UsageCounts = EMPTY;
  try {
    const raw = window.localStorage.getItem(key);
    if (raw) {
      const obj = JSON.parse(raw) as unknown;
      if (obj && typeof obj === 'object' && !Array.isArray(obj)) {
        const clean: Record<string, number> = {};
        for (const [k, v] of Object.entries(obj as Record<string, unknown>)) {
          if (typeof v === 'number' && Number.isFinite(v) && v > 0) clean[k] = v;
        }
        parsed = Object.freeze(clean);
      }
    }
  } catch {
    /* storage unavailable / malformed — honest empty (cold-start) */
  }
  snapshotCache.set(key, parsed);
  return parsed;
}

function notify(): void {
  for (const fn of Array.from(listeners)) {
    try {
      fn();
    } catch (err) {
      console.error('adaptive usageStore listener threw', err);
    }
  }
}

/**
 * Whether the operator has explicitly opted this scope out of adaptation. When
 * true the UI renders the DEFAULT ordering even if usage counts exist — the
 * transparency/reset control writes this flag.
 */
export function isAdaptationDisabled(scope: UsageScope): boolean {
  try {
    return window.localStorage.getItem(optOutKeyFor(scope)) === '1';
  } catch {
    return false;
  }
}

export function setAdaptationDisabled(scope: UsageScope, disabled: boolean): void {
  try {
    if (disabled) window.localStorage.setItem(optOutKeyFor(scope), '1');
    else window.localStorage.removeItem(optOutKeyFor(scope));
  } catch {
    /* ignore — in-memory notify still refreshes chrome */
  }
  notify();
}

/** Read the current usage counts for a scope (frozen snapshot). */
export function getUsageCounts(scope: UsageScope): UsageCounts {
  if (typeof window === 'undefined') return EMPTY;
  return readRaw(usageKeyFor(scope));
}

/**
 * Record one open of `surfaceKey` (a module id) in the given scope. Bumps the
 * count, persists it, and notifies subscribers so the nav/dashboard re-rank.
 */
export function recordSurfaceUse(scope: UsageScope, surfaceKey: string): void {
  if (typeof window === 'undefined' || !surfaceKey) return;
  const key = usageKeyFor(scope);
  const current = readRaw(key);
  const next = Object.freeze({ ...current, [surfaceKey]: (current[surfaceKey] ?? 0) + 1 });
  snapshotCache.set(key, next);
  try {
    window.localStorage.setItem(key, JSON.stringify(next));
  } catch {
    /* storage unavailable — in-memory ranking still works for this session */
  }
  notify();
}

/**
 * Clear the learned signal for a scope (the "reset to default ordering"
 * action). Drops the counts entirely so the UI returns to its honest cold-start
 * default. Does not touch the opt-out flag — callers decide whether reset also
 * re-enables adaptation.
 */
export function resetUsage(scope: UsageScope): void {
  if (typeof window === 'undefined') return;
  const key = usageKeyFor(scope);
  snapshotCache.set(key, EMPTY);
  try {
    window.localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
  notify();
}

export function subscribeUsage(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Reactive hook: returns the live usage counts for a scope. Re-renders the
 * consumer whenever a surface is recorded or the signal is reset. The snapshot
 * is a stable frozen reference between unrelated notifications, so React's
 * `useSyncExternalStore` won't loop.
 */
export function useUsageCounts(scope: UsageScope): UsageCounts {
  const key = usageKeyFor(scope);
  return useSyncExternalStore(
    subscribeUsage,
    () => (typeof window === 'undefined' ? EMPTY : readRaw(key)),
    () => EMPTY
  );
}

/** Reactive hook: whether adaptation is opted out for the scope. */
export function useAdaptationDisabled(scope: UsageScope): boolean {
  return useSyncExternalStore(
    subscribeUsage,
    () => isAdaptationDisabled(scope),
    () => false
  );
}
